import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaBuilding, FaEnvelope, FaLeaf } from 'react-icons/fa';
import api from '../services/api';
import { useTenant } from '../contexts/TenantContext';
import './Login.css';

const isValidEmail = (value) => /\S+@\S+\.\S+/.test(value);

export default function ForgotPassword() {
  const { tenant, setTenant, tenants, tenantsLoading, tenantsError } = useTenant();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [devToken, setDevToken] = useState('');
  const [loading, setLoading] = useState(false);

  const tenantOptions = useMemo(
    () => tenants.map((item) => ({
      id: item.id,
      name: item.name ?? item.nome ?? item.id,
    })),
    [tenants]
  );

  const resetLink = devToken
    ? `/reset-password?token=${encodeURIComponent(devToken)}&tenant=${encodeURIComponent(tenant)}`
    : '';

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!tenant) {
      setFieldError('Selecione o cliente para continuar.');
      return;
    }

    if (!email.trim()) {
      setFieldError('Informe seu e-mail.');
      return;
    }

    if (!isValidEmail(email.trim())) {
      setFieldError('Informe um e-mail válido.');
      return;
    }

    setLoading(true);
    setMessage('');
    setFieldError('');
    setDevToken('');

    try {
      const response = await api.post('/auth/forgot-password', { email: email.trim() });
      setMessage(response.data?.message || 'Se o e-mail existir, enviaremos as instruções de redefinição.');
      setDevToken(response.data?.devToken || '');
    } catch {
      setMessage('Se o e-mail existir, enviaremos as instruções de redefinição.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-copy" aria-label="Recuperação de senha Cultiva Mais">
        <div className="login-copy-badge">
          <FaLeaf />
          Acesso seguro
        </div>
        <h1>Recupere seu acesso ao Cultiva Mais.</h1>
        <p>
          Informe o cliente e o e-mail cadastrado para receber as instruções de redefinição da senha.
        </p>
      </section>

      <section className="login-card" aria-labelledby="forgot-password-title">
        <div className="login-brand">
          <img src="/CultivaMaisLogo.png" alt="Cultiva Mais" />
        </div>

        <div className="login-heading">
          <h2 id="forgot-password-title">Recuperar senha</h2>
          <p>Vamos enviar um caminho seguro para você redefinir sua senha.</p>
        </div>

        {(message || fieldError || tenantsError) && (
          <div className={`login-alert ${message && !fieldError && !tenantsError ? 'is-success' : ''}`} role="alert" aria-live="assertive">
            <strong>{message && !fieldError && !tenantsError ? 'Solicitação recebida.' : 'Atenção.'}</strong>
            <span>{fieldError || tenantsError || message}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="login-field">
            <label htmlFor="forgot-tenant">Cliente</label>
            <div className={`login-control ${fieldError && !tenant ? 'has-error' : ''}`}>
              <FaBuilding className="login-control-icon" aria-hidden="true" />
              <select
                id="forgot-tenant"
                value={tenant}
                onChange={(event) => {
                  setTenant(event.target.value);
                  setFieldError('');
                  setMessage('');
                }}
                disabled={tenantsLoading || loading}
                required
                aria-invalid={Boolean(fieldError && !tenant)}
              >
                <option value="">
                  {tenantsLoading ? 'Carregando clientes...' : 'Selecione o cliente'}
                </option>
                {tenantOptions.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="login-field">
            <label htmlFor="forgot-email">E-mail</label>
            <div className={`login-control ${fieldError && tenant ? 'has-error' : ''}`}>
              <FaEnvelope className="login-control-icon" aria-hidden="true" />
              <input
                id="forgot-email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setFieldError('');
                  setMessage('');
                }}
                autoComplete="email"
                disabled={loading}
                required
                aria-invalid={Boolean(fieldError && tenant)}
              />
            </div>
          </div>

          <button type="submit" className="login-button" disabled={loading}>
            {loading && <span className="login-spinner" aria-hidden="true" />}
            {loading ? 'Enviando...' : 'Enviar instruções'}
          </button>
        </form>

        {devToken && (
          <div className="login-dev-token">
            <strong>Teste local: token gerado</strong>
            <code>{devToken}</code>
            <Link to={resetLink}>Abrir tela de redefinição</Link>
          </div>
        )}

        <div className="login-support">
          <Link className="login-back-link" to="/login">
            <FaArrowLeft aria-hidden="true" />
            Voltar ao login
          </Link>
        </div>
      </section>
    </main>
  );
}
